import CustomDrawer from "@/components/drawer";
import PersianSupportNumberInput from "@/components/persianNumberInput";
import IncreaseWallet from "@/modules/home/increaseWallet";
import useGetWallet from "@/pages/dashboard/home/hooks/useGetWalletInfo";
import useCalculateDiscountAmount from "@/services/discount/hooks/useCalculateDiscountAmount";
import useCalculateDiscountCustomerGroupIdAmount from "@/services/discount/hooks/useCalculateDiscountCustomerGroupIdAmount";
import useCreateDiscountByCustomerGroup from "@/services/discount/hooks/useCreateDiscountByCustomerGroup";
import useCreateDiscountByDiscountGroup from "@/services/discount/hooks/useCreateDiscountByDiscountGroup";
import {
  CreateCustomerGroupDiscountPayload,
  CreateDiscountGroupForm,
  CreateDiscountGroupPayload,
} from "@/services/discount/type";
import Num2persian from "@/services/persianUtiles/convertPriceToPersian";
import useAlertStore from "@/store/alert";
import {
  Button,
  Card,
  Center,
  Group,
  NativeSelect,
  rem,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconPercentage } from "@tabler/icons-react";
import moment from "moment-jalaali";
import { useEffect, useState } from "react";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePicker, { DateObject } from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import "react-multi-date-picker/styles/layouts/mobile.css";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { v4 as uuidv4 } from "uuid";
import { persistTempDiscount } from "./helper";
import { DateSelector } from "@/components/selectorDatePicker";

const schema = Yup.object().shape({
  discountType: Yup.string(),
  amount: Yup.string().when("discountType", (discountType, schema) => {
    return discountType.toString() === "0"
      ? schema
          .test(
            "is-greater-than-zero",
            "مقدار باید بیشتر از صفر باشد",
            (value) => Number(value) > 0
          )
          .required("مقدار باید بیشتر از صفر باشد")
      : schema;
  }),
  count: Yup.string()
    .typeError("مقدار را وارد نمایید")
    .required("تعداد را وارد نمایید"),
  expireDate: Yup.string()
    .typeError("مقدار را وارد نمایید")
    .required("تاریخ را وارد نمایید"),
  percent: Yup.string().when("discountType", (discountType, schema) => {
    return discountType.toString() === "1"
      ? schema
          .test(
            "is-greater-than-zero",
            "مفدار باید بیشتر از 1 و کمتر از 100 باشد",
            (value) => Number(value) > 0 && Number(value) < 100
          )
          .required("مقدار را وارد نمایید")
      : schema;
  }),
});

function DiscountFormByCustomerGroup() {
  const { state } = useLocation();
  const [stateInfo] = useState(state);

  const [amount, setAmount] = useState<number>();

  const form = useForm<CreateDiscountGroupForm>({
    initialValues: {
      id: uuidv4(),
      amount: undefined,
      count: "1",
      customerGroupId: stateInfo?.type === 0 ? stateInfo?.group?.id : undefined,
      discountGroupId: stateInfo?.type === 1 ? stateInfo?.group?.id : undefined,
      discountType: "0",
      expireDate: undefined,
      maximumAmount: undefined,
      percent: undefined,
      time: undefined,
      minimumAmount: undefined,
    },
    validate: yupResolver(schema),
  });

  const [opened, { open, close }] = useDisclosure(false);

  const [openAmount, { open: setOpenAmount, close: setCloseAmount }] =
    useDisclosure(false);

  const { data: wallet } = useGetWallet();

  const { mutate: mutateByDiscountGroup, isLoading: isLoadingDiscountGroup } =
    useCreateDiscountByDiscountGroup();

  const { mutate, isLoading } = useCreateDiscountByCustomerGroup();

  const { mutate: checkAmount, isLoading: isCheckAmountLoading } =
    useCalculateDiscountAmount({ setAmount, setOpenAmount });

  const {
    mutate: checkAmountCustomerId,
    isLoading: isCheckAmountCustomerIdLoading,
  } = useCalculateDiscountCustomerGroupIdAmount({
    setAmount,
    setOpenAmount,
  });

  const discountType = form.values.discountType;
  const { setValues } = form;

  useEffect(() => {
    setValues({
      amount: "0",
      percent: "0",
    });
  }, [discountType, setValues]);

  const handleFormSubmit = (values: CreateDiscountGroupForm) => {
    const date = moment(
      values?.expireDate
        ?.setHour(values?.time?.hour || 0)
        .setMinute(values?.time?.minute || 0)
        .toDate() || ""
    )
      .locale("en")
      .toISOString();
    const payload: CreateCustomerGroupDiscountPayload = {
      amount: values?.amount ? Number(values?.amount) : undefined,
      count: Number(values?.count),
      customerGroupId: stateInfo?.group?.id,
      discountType: Number(values?.discountType),
      expireDate: date,
      maximumAmount: values?.maximumAmount
        ? Number(values?.maximumAmount)
        : undefined,
      percent: values?.percent ? Number(values?.percent) : undefined,
      minimumAmount: values?.minimumAmount
        ? Number(values?.minimumAmount)
        : undefined,
      smsTemplateId: stateInfo?.selectedTemplate?.id,
    };

    if (stateInfo?.type === 0) {
      checkAmountCustomerId(payload);
      return;
    } else {
      const payload: CreateDiscountGroupPayload = {
        amount: values?.amount ? Number(values?.amount) : undefined,
        count: Number(values?.count),
        discountGroupId: stateInfo?.group?.id,
        discountType: Number(values?.discountType),
        expireDate: date,
        maximumAmount: values?.maximumAmount
          ? Number(values?.maximumAmount)
          : undefined,
        percent:
          values?.discountType === "1" ? Number(values?.percent) : undefined,
        minimumAmount: values?.minimumAmount
          ? Number(values?.minimumAmount)
          : undefined,
        smsTemplateId: stateInfo?.selectedTemplate?.id,
      };
      checkAmount(payload);
    }
  };

  const { addAlert } = useAlertStore((state) => state);

  const openIncreaseWallet = () => {
    setCloseAmount();
    open();
    persistTempDiscount(
      form?.values,
      stateInfo?.group,
      stateInfo?.selectedTemplate,
      addAlert
    );
  };

  const createDiscount = () => {
    const values = form.values;
    const date = moment(
      values?.expireDate
        ?.setHour(values?.time?.hour || 0)
        .setMinute(values?.time?.minute || 0)
        .toDate() || ""
    )
      .locale("en")
      .toISOString();

    const payload: CreateCustomerGroupDiscountPayload = {
      amount: values?.amount ? Number(values?.amount) : undefined,
      count: Number(values?.count),
      customerGroupId: stateInfo?.group?.id,
      discountType: Number(values?.discountType),
      expireDate: date,
      maximumAmount: values?.maximumAmount
        ? Number(values?.maximumAmount)
        : undefined,
      percent: values?.percent ? Number(values?.percent) : undefined,
      minimumAmount: values?.minimumAmount
        ? Number(values?.minimumAmount)
        : undefined,
      smsTemplateId: stateInfo?.selectedTemplate?.id,
    };

    if (stateInfo?.type === 0) {
      mutate(payload);
      return;
    } else {
      const payload: CreateDiscountGroupPayload = {
        amount: values?.amount ? Number(values?.amount) : undefined,
        count: Number(values?.count),
        discountGroupId: stateInfo?.group?.id,
        discountType: Number(values?.discountType),
        expireDate: date,
        maximumAmount: values?.maximumAmount
          ? Number(values?.maximumAmount)
          : undefined,
        percent:
          values?.discountType === "1" ? Number(values?.percent) : undefined,
        minimumAmount: values?.minimumAmount
          ? Number(values?.minimumAmount)
          : undefined,
        smsTemplateId: stateInfo?.selectedTemplate?.id,
      };
      mutateByDiscountGroup(payload);
    }
  };

  const [date, setDate] = useState<DateObject>(
    new DateObject({ calendar: persian, locale: persian_fa })
  );

  const handleDateChange = (newDate: DateObject) => {
    setDate(newDate);
  };
  // const isLeapYear = (year) => {
  //   return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  // };

  // const getDaysInMonth = (date) => {
  //   const daysInMonth = {
  //     1: 31, // Farvardin
  //     2: 31, // Ordibehesht
  //     3: 31, // Khordad
  //     4: 31, // Tir
  //     5: 30, // Mordad
  //     6: 30, // Shahrivar
  //     7: 30, // Mehr
  //     8: 30, // Aban
  //     9: 30, // Azar
  //     10: 30, // Dey
  //     11: 30, // Bahman
  //     12: 29, // Esfand (30 in leap years)
  //   };

  //   if (date.month.index + 1 === 12 && isLeapYear(date.year)) {
  //     return 30;
  //   }
  //   return daysInMonth[date.month.index + 1] || 29;
  // };

  // const days = Array.from({ length: getDaysInMonth(date) }, (_, i) => i + 1);
  // const months = date.locale.months.map((x) => x[0]);
  // const years = Array.from({ length: 50 }, (_, i) => date.year - 25 + i);

  // const handleDayChange = (e) => {
  //   setDate((prevDate) => {
  //     const newDate = new DateObject(prevDate);
  //     newDate["day"] = parseInt(e.target.value);
  //     return new DateObject(newDate);
  //   });
  // };

  // const handleMonthChange = (e) => {
  //   setDate((prevDate) => {
  //     const newDate = new DateObject(prevDate);
  //     console.log(months.findIndex((m) => m === e.target.value) + 1);
  //     newDate["month"] = months.findIndex((m) => m === e.target.value) + 1;
  //     return new DateObject(newDate);
  //   });
  // };

  // const handleYearChange = (e) => {
  //   setDate((prevDate) => {
  //     const newDate = new DateObject(prevDate);
  //     newDate["year"] = e.target.value;
  //     return new DateObject(newDate);
  //   });
  // };

  // const style = {
  //   display: "inline-block",
  //   width: "90px",
  //   fontSize: "16px",
  //   margin: "0 5px",
  // };

  return (
    <>
      <form onSubmit={form.onSubmit(handleFormSubmit)}>
        <Stack
          styles={{
            root: {
              flexGrow: 1,
            },
          }}
          mb="lg"
        >
          <Stack
            styles={{
              root: {
                flexGrow: 1,
              },
            }}
          >
            <Stack gap="0">
              <Text size="lg">نوع تخفیف</Text>
              <SegmentedControl
                size="md"
                data={[
                  { value: "0", label: "مبلغی" },
                  { value: "1", label: "درصدی" },
                ]}
                {...form.getInputProps("discountType")}
              />
            </Stack>
            <Stack gap="5">
              {form?.values.discountType === "1" ? (
                <PersianSupportNumberInput
                  rightSection={<IconPercentage size="15" />}
                  size="lg"
                  withAsterisk
                  withThousandsSeparator
                  styles={{
                    input: {
                      direction: "ltr",
                      textAlign: "left",
                      paddingLeft:
                        form?.values.discountType === "1" ? "2.5rem" : "",
                    },
                    error: {
                      fontSize: "0.8rem",
                    },
                  }}
                  label={"درصد تحفیف"}
                  onChangeHandler={(e) =>
                    setValues({
                      percent: e,
                    })
                  }
                  {...form.getInputProps("percent")}
                />
              ) : (
                <PersianSupportNumberInput
                  size="lg"
                  withAsterisk
                  withThousandsSeparator
                  styles={{
                    input: {
                      direction: "ltr",
                      textAlign: "left",
                    },
                    error: {
                      fontSize: "0.8rem",
                    },
                  }}
                  label={"مبلغ تخفیف"}
                  onChangeHandler={(e) =>
                    setValues({
                      amount: e,
                    })
                  }
                  {...form.getInputProps("amount")}
                />
              )}

              {form?.values.discountType === "0" ? (
                Number(form?.values?.amount) > 0 && (
                  <Text size="xs" c="blue">
                    {Num2persian(form?.values?.amount?.toString() || "0")} ریال
                  </Text>
                )
              ) : (
                <Group gap="xs">
                  <Text size="xs" c="blue">
                    درصد بین
                  </Text>
                  <Text size="xs" c="blue">
                    ( 1 تا 100 )
                  </Text>
                </Group>
              )}
            </Stack>

            <Stack gap="0">
              <PersianSupportNumberInput
                size="lg"
                withThousandsSeparator
                styles={{
                  input: {
                    direction: "ltr",
                    textAlign: "left",
                  },
                  error: {
                    fontSize: "0.8rem",
                  },
                }}
                label={"حداقل مبلغ خرید"}
                onChangeHandler={(e) =>
                  setValues({
                    minimumAmount: e,
                  })
                }
                {...form.getInputProps("minimumAmount")}
              />
              {Number(form?.values?.minimumAmount) > 0 && (
                <Text size="xs" c="blue">
                  {Num2persian(form?.values?.minimumAmount?.toString() || "0")}{" "}
                  ریال
                </Text>
              )}
            </Stack>

            {form?.values.discountType === "1" ? (
              <Stack gap="5">
                <PersianSupportNumberInput
                  size="lg"
                  withThousandsSeparator
                  styles={{
                    input: {
                      direction: "ltr",
                      textAlign: "left",
                    },
                    error: {
                      fontSize: "0.8rem",
                    },
                  }}
                  label="سقف تخفیف"
                  onChangeHandler={(e) =>
                    setValues({
                      maximumAmount: e,
                    })
                  }
                  {...form.getInputProps("maximumAmount")}
                />
                {Number(form?.values?.maximumAmount) > 0 && (
                  <Text size="xs" c="blue">
                    {Num2persian(
                      form?.values?.maximumAmount?.toString() || "0"
                    )}{" "}
                    ریال
                  </Text>
                )}
              </Stack>
            ) : null}

            <Stack gap="5">
              <NativeSelect
                size="lg"
                data={["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]}
                styles={{
                  error: {
                    fontSize: "0.8rem",
                  },
                }}
                label="تعداد دفعات استفاده"
                {...form.getInputProps("count")}
              />
            </Stack>
            {form?.values?.expireDate?.toDate().toISOString()}

            {form?.values?.expireDate && (
              <DatePicker
                className="rmdp-mobile"
                calendar={persian}
                locale={persian_fa}
                format="HH:mm"
                disableDayPicker
                plugins={[<TimePicker hideSeconds />]}
                value={form.values.time}
                onChange={(value) => {
                  setValues({ time: value || undefined });
                }}
                render={
                  <TextInput
                    size="lg"
                    label="ساعت انقضا کد تخفیف"
                    description="کاربر تا این ساعت میتواند از کد تخفیف استفاده کند"
                    {...form.getInputProps("time")}
                    onClick={() => setValues({ time: new DateObject() })}
                    styles={{
                      input: {
                        direction: "ltr",
                        textAlign: "left",
                      },
                      error: {
                        fontSize: "0.8rem",
                      },
                    }}
                    readOnly
                  />
                }
              />
            )}
          </Stack>
          <DateSelector selectedDate={date} onChange={handleDateChange} />
          {/* <div style={{ textAlign: "center" }}>
            <div>
              <select value={date.day} onChange={handleDayChange} style={style}>
                {days.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
              <select
                value={date.month.name}
                onChange={handleMonthChange}
                style={style}
              >
                {months.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
              <select
                value={date.year}
                onChange={handleYearChange}
                style={style}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <DatePicker
              value={date}
              onChange={setDate}
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-center"
            />
          </div> */}
          <Stack>
            <Button
              fullWidth
              size="lg"
              type="submit"
              loading={
                isLoading ||
                isLoadingDiscountGroup ||
                isCheckAmountLoading ||
                isCheckAmountCustomerIdLoading
              }
              mt="md"
            >
              محاسبه قیمت
            </Button>
          </Stack>
        </Stack>
      </form>

      <CustomDrawer
        opened={openAmount}
        onClose={setCloseAmount}
        close={setCloseAmount}
        position="bottom"
        radius="md"
        custom="raiseAmount"
      >
        <Stack justify="space-between" h={"350px"}>
          <Stack
            styles={{
              root: {
                flexGrow: 1,
              },
            }}
          >
            <Card withBorder>
              <Text>مبلغ محاسبه شده : </Text>
              <Center>
                <Group gap="xs" pt="md">
                  <Text size={rem(30)} fw="900">
                    {amount?.toLocaleString()}
                  </Text>
                  <Text>ریال</Text>
                </Group>
              </Center>
            </Card>
          </Stack>

          {(wallet?.amount || 0) < (amount || 0) ? (
            <Button size="lg" onClick={openIncreaseWallet}>
              افزایش موجودی اعتبار پیامکی
            </Button>
          ) : (
            <Button
              size="lg"
              loading={isLoadingDiscountGroup || isLoading}
              onClick={createDiscount}
            >
              ثبت
            </Button>
          )}
        </Stack>
      </CustomDrawer>

      <CustomDrawer
        opened={opened}
        onClose={close}
        close={close}
        title="افزایش موجودی اعتبار پیامکی"
        radius="md"
        position="bottom"
        custom="increase"
      >
        <IncreaseWallet defaultAmount={amount} />
      </CustomDrawer>
    </>
  );
}

export default DiscountFormByCustomerGroup;
